import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Aperture,
    Camera,
    DoorOpen,
    Image as ImageIcon,
    ImagePlus,
    Loader2,
    Ratio,
    Scan,
    Sofa,
    Trash2,
    Wallpaper,
    X,
} from 'lucide-react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, Lightformer, ContactShadows, TransformControls, Grid } from '@react-three/drei';
import { EffectComposer, DepthOfField, BrightnessContrast, ToneMapping } from '@react-three/postprocessing';
import { ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { PlaceholderRoom } from './StudioRoom';
import { StudioSectionHeader, StudioMarkSlider, StudioPill } from './studio-ui';
import { StudioDock, StudioDockItemDef } from './StudioDock';
import { useShotCamera, CameraObject, PiPView, CamView, EDITOR_FOCAL_LENGTH, CameraPathAnimator, ReleaseContextOnUnmount } from './StudioCamera';
import { hideCaptureHelpers, restoreCaptureHelpers } from './studio-capture';
import { getProductModelObjectUrl } from '../../edit-product-v3/api/productV2Api';
import {
    FloorDropResolver, PlacedProp, ScenePropObject, describeSceneProps,
    UniformScaleHandle, PROP_SCALE_MIN, PROP_SCALE_MAX,
} from './StudioProps';
import { StudioProp, useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import ErrorBoundary from '../../../../common/components/ErrorBoundary';
import { LENS_PRESETS, resolveNamedCameraAngle, flatProductElevation } from './camera-angles';
import { useStudioTakes } from './use-studio-takes';
import { takeDockItems } from './TakeDockPanels';

/** The bucket a vendor's own generated pieces land in. */
const MY_LIBRARY = 'my library';

/** Floating toolbar chrome over the 3D viewport — shared by every gizmo bar. */
export const STUDIO_TOOLBAR_CLASS = 'absolute z-[2] flex items-center gap-1 rounded-lg border bg-background/90 p-1 backdrop-blur-sm';

/** Thin divider between toolbar groups. */
export const StudioToolbarDivider: React.FC = () => (
    <div className="mx-0.5 h-[18px] w-px bg-border" />
);

/**
 * Shown in place of the 3D canvas when the model fails to load or parse.
 * The underlying error is logged by ErrorBoundary.componentDidCatch.
 */
const Studio3DLoadFallback: React.FC = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted px-6 text-center">
        <span className="text-sm font-medium text-foreground">
            Could not load the 3D model
        </span>
        <span className="max-w-[380px] text-sm leading-normal text-muted-foreground">
            The file could not be read. It may be missing or stored in a format this
            preview can&apos;t decrypt — see the browser console for details.
        </span>
    </div>
);

/**
 * Everything about a posed studio scene that a reload would otherwise destroy.
 *
 * The studio already kept its framing across close/reopen — but only in refs on
 * a component that stays mounted, so a page reload took the lot: the camera, the
 * piece's pose, the furniture placed around it, the room. The vendor's captured
 * PNG survived on the node and the scene that produced it did not, so the only
 * way to adjust a shot was to build it again from nothing.
 *
 * Stored on the recipe node's inputs, which is already how the snapshot itself
 * survives, so it rides the canvas autosave (and its reload flush) rather than
 * inventing a second persistence path.
 *
 * `v` is here so a later shape change can be recognised and ignored rather than
 * applied half-way to a scene it does not fit.
 */
export interface StudioSceneState {
    v: 1;
    /** The shot camera — position and target. */
    shot: CamView | null;
    lensMm: number;
    aperture: number;
    aspectRatio: string;
    /** The hero piece's posed transform, as the gizmo left it. */
    model: { pos: [number, number, number]; rot: [number, number, number]; scale: [number, number, number] } | null;
    /** Library furniture WITH its placement — not the prompt's name/category summary. */
    props: PlacedProp[];
    room: {
        on: boolean;
        floorW: number;
        floorD: number;
        wallH: number;
        walls: { back: boolean; left: boolean; right: boolean };
    };
}

interface Furniture3DStudioWidgetProps {
    /** GLB / GLTF model URL for the linked furniture product. */
    modelPath: string;
    /** Product UUID — when set, the model is fetched DECRYPTED from the backoffice endpoint. */
    productUuid?: string;
    /** URL of a previously captured snapshot, if any. */
    snapshotUrl?: string;
    /** Recipe aspect ratio (e.g. '16:9') — the studio viewport matches it so framing = output. */
    aspectRatio?: string;
    /** Two-way sync: called when the aspect is changed inside the studio, so the
     *  recipe's aspect stays in step (and vice-versa via the aspectRatio prop). */
    onAspectRatioChange?: (value: string) => void;
    /** From Reference: show this image as a faint alignment guide over the canvas
     *  (HTML overlay only — never part of the capture) so the vendor can pose the
     *  product to match the reference's object position/angle. */
    referenceImageUrl?: string;
    /** True when the product is a flat floor covering (rug/carpet/mat). The model
     *  is auto-laid FLAT on the ground so any camera angle (incl. top view)
     *  captures it lying flat, not standing up as a tilted board. */
    isRug?: boolean;
    /** Allow Room mode (pose the piece in a placeholder room the worker reskins).
     *  Only makes sense for room-building recipes (e.g. lifestyle) — hidden for
     *  Product Photography, whose presets rely on a transparent product cutout. */
    allowRoomMode?: boolean;
    /** Upload the captured frame; returns its URL. */
    onCapture: (file: File) => Promise<string>;
    /** Parent persists the uploaded URL (+ the camera view + lens/aperture/aspect + any blockouts + room flag) onto the recipe node's inputs. */
    onSnapshotUpdated: (url: string, cameraView?: string, camera?: { lensMm: number; aperture: number; aspectRatio: string }, blockouts?: Array<{ type: string; label: string; where: string }>, furnitureRoom?: boolean,
        sceneProps?: Array<{ name: string; category: string }>,
        /**
         * The named angle this frame was actually posed to, or null when the
         * vendor framed it by hand.
         *
         * Distinct from the node's stored `cameraAngle`, which is what was last
         * *asked* for. The two part company the moment someone re-frames a shot
         * themselves, and reporting the request as though it were the result is
         * how a node ends up describing a photo it never took.
         */
        capturedAngle?: string | null) => void;
    /**
     * Pose to this named angle and capture without anyone opening the studio.
     *
     * The studio is reused rather than reimplemented: a second renderer would
     * have to match this one's lighting, lens and alpha handling, and would
     * drift from it the first time either changed. The dialog opens for real,
     * frames itself and closes, so the frame is the one the vendor would have
     * got — and what is on screen is what happened.
     */
    headlessAngle?: string | null;
    /** Called once a headless capture finishes, with a message if it failed. */
    onHeadlessDone?: (error?: string) => void;
    /** The scene as it was last captured, restored on mount. */
    sceneState?: StudioSceneState | null;
    /**
     * Persist the scene. Called at CAPTURE, deliberately — not on close.
     *
     * What is stored has to be the scene that produced the stored frame. Saving
     * on close would let an idle orbit after the capture overwrite it, and the
     * studio would then reopen framed somewhere the snapshot never saw.
     */
    onSceneStateChange?: (scene: StudioSceneState) => void;
    /** Draw your own launcher instead of the thumbnail tile; call `open` to open the studio. */
    renderTrigger?: (open: () => void) => React.ReactNode;
    /** Open the studio with this dock panel already showing (e.g. 'video'). */
    initialDock?: string;
    /**
     * A recorded camera move is offered back as a "take": the viewport video
     * (uploaded), its first frame and its length. Shows a "Use this take"
     * button on the recording; the studio closes once it is used.
     */
    onTakeRecorded?: (take: { url: string; posterUrl?: string; durationSec: number }) => void;
}

// The panel primitives live in ./studio-ui (shared with the take panels without
// an import cycle) and are re-exported here for existing importers.
export { StudioSectionHeader, StudioMarkSlider, StudioPill };

// describeCameraView now lives in ./StudioCamera (shared by both studios) and is
// re-exported here for existing importers.
export { describeCameraView } from './StudioCamera';

/**
 * Is there actually a subject in the frame we just rendered?
 *
 * Read back a coarse grid of the drawing buffer rather than the whole thing —
 * a capture is already ~2000px square, and this only has to answer "anything or
 * nothing". Product mode renders a transparent cutout, so coverage is alpha;
 * room mode renders opaque on white, so it is "not white". Below the threshold
 * the frame is empty and the caller should refuse it rather than upload it.
 */
function bufferHasSubject(renderer: THREE.WebGLRenderer, opaqueOnWhite: boolean): boolean {
    try {
        const gl = renderer.getContext();
        const w = gl.drawingBufferWidth;
        const h = gl.drawingBufferHeight;
        if (!w || !h) return false;
        const step = Math.max(1, Math.floor(Math.min(w, h) / 96)); // ~96×96 samples
        const row = new Uint8Array(w * 4);
        let hits = 0;
        let seen = 0;
        for (let y = 0; y < h; y += step) {
            gl.readPixels(0, y, w, 1, gl.RGBA, gl.UNSIGNED_BYTE, row);
            for (let x = 0; x < w; x += step) {
                const i = x * 4;
                seen += 1;
                if (opaqueOnWhite) {
                    if (row[i] < 244 || row[i + 1] < 244 || row[i + 2] < 244) hits += 1;
                } else if (row[i + 3] > 8) hits += 1;
            }
        }
        // A tenth of a percent of the frame is far below any real product and
        // far above the stray antialiased pixel.
        return seen > 0 && hits / seen > 0.001;
    } catch {
        // Never fail a good capture because the check itself could not run.
        return true;
    }
}

/** Lens presets (focal length in mm) — drive a real PerspectiveCamera focal
 *  length. Defined in camera-angles so the recipe schema can offer them without
 *  importing a WebGL stack; re-exported here for existing importers. */
export { LENS_PRESETS };

export function lensCharacter(mm: number): string {
    if (mm <= 28) return 'wide-angle';
    if (mm < 45) return 'environmental';
    if (mm <= 60) return 'natural';
    if (mm <= 105) return 'portrait';
    return 'telephoto (compressed)';
}

/** Aperture f-stops — drive the depth-of-field (background blur) applied at render.
 *  Wider aperture (smaller f-number) = shallower DOF = more blur. f/16 ≈ all sharp. */
export const APERTURE_STOPS: number[] = [1.4, 2, 2.8, 4, 5.6, 8, 11, 16];

export function apertureCharacter(f: number): string {
    if (f <= 2) return 'very shallow — dreamy blur';
    if (f <= 4) return 'shallow — soft background';
    if (f <= 8) return 'moderate depth of field';
    if (f < 16) return 'deep — mostly sharp';
    return 'everything sharp';
}

/** Map an aperture f-stop to real DepthOfField params. Wider (smaller f) = more
 *  blur AND a thinner in-focus slab (so the model's own front-to-back depth blurs
 *  selectively). worldFocusRange is in world units — the model spans ~2 units, so a
 *  ~0.35u slab is a thin focus plane; ~4u keeps the whole model sharp. f/16 → sharp. */
export function apertureToDof(f: number): { bokehScale: number; worldFocusRange: number } {
    const shallow = Math.min(1, Math.max(0, (Math.log2(16) - Math.log2(f)) / (Math.log2(16) - Math.log2(1.4))));
    return {
        bokehScale: shallow * 9, // 0 (f/16) → 9 (f/1.4)
        worldFocusRange: 0.35 + (1 - shallow) * 4.0, // f/1.4 ≈ 0.35u (thin) → f/16 ≈ 4.35u (all sharp)
    };
}

export function aspectToNumber(ar?: string): number {
    if (!ar || !ar.includes(':')) return 1;
    const [w, h] = ar.split(':').map(Number);
    return w > 0 && h > 0 ? w / h : 1;
}

/** Posing backdrop colours — COSMETIC only (never captured). Lets the vendor
 *  switch the viewport behind the product so a white piece reads against dark
 *  (or a dark piece against light) while framing. The capture is always a
 *  transparent cutout regardless of this choice. */
const BACKDROP_PRESETS: { v: string; label: string }[] = [
    { v: '#EDEFF2', label: 'Light' },
    { v: '#C0C5CE', label: 'Mid' },
    { v: '#33383F', label: 'Dark' },
];

/** Output aspect ratios — chosen INSIDE the studio; the capture defines the final frame. */
export const ASPECT_PRESETS: { v: string; label: string }[] = [
    { v: '1:1', label: '1:1' },
    { v: '4:5', label: '4:5' },
    { v: '3:4', label: '3:4' },
    { v: '16:9', label: '16:9' },
    { v: '9:16', label: '9:16' },
];

/**
 * 3D Studio — the camera-control step for the `studio-3d` recipe.
 *
 * Inline: a launcher (with a thumbnail of the captured view, if any).
 * Dialog: a large viewport with a FREE Blender-style camera — orbit, dolly/zoom,
 * pan — plus a real LENS control (focal length in mm: wide ↔ telephoto). The
 * vendor frames the furniture on a neutral backdrop + soft contact shadow,
 * captures it, and the snapshot is uploaded to inputs.snapshotUrl. The worker
 * then renders a realistic room around that exact viewpoint.
 */
export const Furniture3DStudioWidget: React.FC<Furniture3DStudioWidgetProps> = (props) => {
    const { modelPath, snapshotUrl, renderTrigger } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    if (!modelPath) {
        return (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground italic">
                {t('CONTENT_STUDIO.STUDIO_NO_MODEL', 'This product has no 3D model, so the 3D studio is not available.')}
            </div>
        );
    }

    return (
        <>
            {renderTrigger ? renderTrigger(() => setOpen(true)) : <div
                className="group relative flex aspect-square w-full cursor-pointer flex-col items-center justify-center gap-2.5 overflow-hidden rounded-lg border bg-muted transition-colors hover:border-input"
                onClick={() => setOpen(true)}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {snapshotUrl ? (
                    <img
                        src={snapshotUrl}
                        alt=""
                        className="absolute inset-0 h-full w-full object-contain"
                    />
                ) : (
                    <div className="text-muted-foreground opacity-40 [&_svg]:block">
                        <svg width="46" height="46" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="40.887 14.25 24 4.5 7.113 14.25 7.113 33.75 24 43.5 40.887 33.75 40.887 14.25" />
                            <polyline points="7.113 14.25 24 24 40.887 14.25" />
                            <line x1="24" y1="43.5" x2="24" y2="24" />
                            <polygon points="31.12 11.668 31.12 28.111 16.88 36.332 16.88 19.889 31.12 11.668" />
                        </svg>
                    </div>
                )}
                <div className="absolute bottom-2.5 left-1/2 flex max-w-[calc(100%-1.25rem)] -translate-x-1/2 items-center gap-1.5 rounded-lg border bg-background/90 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-foreground backdrop-blur-md transition-colors group-hover:border-input">
                    <Scan className="size-3.5 shrink-0" />
                    <span className="truncate">{snapshotUrl ? t('CONTENT_STUDIO.STUDIO_REFRAME', 'Re-frame') : t('CONTENT_STUDIO.STUDIO_OPEN', 'Open 3D Studio')}</span>
                </div>
            </div>}

            {/* `open` for a headless capture too — the dialog is what owns the
                scene, and it renders invisibly in that mode. */}
            <StudioDialog
                open={open || !!props.headlessAngle}
                onClose={() => setOpen(false)}
                {...props}
            />
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Dialog — the actual 3D workspace.
// ═══════════════════════════════════════════════════════════════════════

const StudioDialog: React.FC<Furniture3DStudioWidgetProps & { open: boolean; onClose: () => void }> = ({
    open,
    onClose,
    modelPath,
    productUuid,
    aspectRatio,
    onAspectRatioChange,
    referenceImageUrl,
    isRug,
    allowRoomMode = false,
    onCapture,
    onSnapshotUpdated,
    headlessAngle,
    onHeadlessDone,
    sceneState,
    onSceneStateChange,
    initialDock,
    onTakeRecorded,
}) => {
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    // Scene + camera captured from onCreated so the capture can force a plain,
    // transparent render directly (bypassing the post-processing loop).
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.Camera | null>(null);
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [focalLength, setFocalLength] = useState(sceneState?.lensMm ?? 50);
    const [aperture, setAperture] = useState(sceneState?.aperture ?? 16); // f/16 — everything sharp by default
    const [aspectRatioSel, setAspectRatioSel] = useState(aspectRatio || sceneState?.aspectRatio || '1:1'); // chosen in-studio
    // Keep the studio viewport in step with the recipe's aspect ratio — changing
    // it on the recipe toolbar instantly reshapes the studio here.
    useEffect(() => {
        if (aspectRatio && aspectRatio !== aspectRatioSel) setAspectRatioSel(aspectRatio);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspectRatio]);
    // From Reference alignment guide — overlay opacity (0 = hidden).
    const [refOpacity, setRefOpacity] = useState(0.4);
    // In-canvas object gizmo — TransformControls attaches to the model group.
    // The rotation/position is mutated directly on the object (baked into the
    // capture), so there's no React rotation state to keep in sync.
    const [transformMode, setTransformMode] = useState<'rotate' | 'translate'>('rotate');
    // Posing backdrop colour (cosmetic — never captured; the capture is a
    // transparent cutout). Defaults to the neutral mid grey.
    const [previewBg, setPreviewBg] = useState('#C0C5CE');
    // View toggles — show/hide the ground grid and the object gizmo independently.
    const [showGrid, setShowGrid] = useState(true);
    const [showGizmo, setShowGizmo] = useState(false);
    // Hide the gizmo helper during capture so it isn't baked into the frame.
    const [capturing, setCapturing] = useState(false);
    const [modelObj, setModelObj] = useState<THREE.Group | null>(null);
    // The same group as a ref: the headless capture runs inside an effect that
    // closed over the old state, so it needs a handle that is current.
    const modelObjRef = useRef<THREE.Group | null>(null);
    // Orbit guard rails, stashed while a named angle is posed (see below).
    const orbitLimitsRef = useRef<{
        minPolarAngle: number; maxPolarAngle: number; minDistance: number; maxDistance: number;
    } | null>(null);
    const initialTransform = useRef<{ pos: THREE.Vector3; rot: THREE.Euler } | null>(null);
    // The piece's posed transform, persisted across close/reopen (the Canvas
    // unmounts on close, so the gizmo-mutated transform would otherwise reset).
    const savedModelTransform = useRef<{ pos: [number, number, number]; rot: [number, number, number]; scale: [number, number, number] } | null>(sceneState?.model ?? null);
    const handleModelRef = React.useCallback((g: THREE.Group | null) => {
        setModelObj(g);
        modelObjRef.current = g;
        if (g) {
            initialTransform.current = { pos: g.position.clone(), rot: g.rotation.clone() };
            const s = savedModelTransform.current;
            if (s) { g.position.set(s.pos[0], s.pos[1], s.pos[2]); g.rotation.set(s.rot[0], s.rot[1], s.rot[2]); g.scale.set(s.scale[0], s.scale[1], s.scale[2]); }
        }
    }, []);
    const saveModelState = () => {
        const g = modelObj;
        if (!g) return;
        savedModelTransform.current = {
            pos: [g.position.x, g.position.y, g.position.z],
            rot: [g.rotation.x, g.rotation.y, g.rotation.z],
            scale: [g.scale.x, g.scale.y, g.scale.z],
        };
    };
    const resetTransform = () => {
        const g = modelObj;
        if (!g || !initialTransform.current) return;
        g.position.copy(initialTransform.current.pos);
        g.rotation.copy(initialTransform.current.rot);
    };
    // DOF focus point — defaults to the product centre, click-to-focus updates it.
    const [focusTarget, setFocusTarget] = useState(() => new THREE.Vector3(0, 0.9, 0));
    const dof = apertureToDof(aperture);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const controlsRef = useRef<any>(null);
    // Persisted across open/close (this component stays mounted; only the Canvas
    // inside the Dialog remounts) so re-opening restores the last framing. The
    // SHOT view is what RestoreCamera reloads; the free editor view is kept aside.
    const savedCameraRef = useRef<CamView | null>(sceneState?.shot ?? null);
    const savedEditCameraRef = useRef<CamView | null>(null);

    // ── Phase 3: the SHOT camera is separate from the editor view (see StudioCamera).
    const { camMode, setCamMode, shotViewRef, toggleCamMode, currentShotView, shotCameraViewText, captureFromShot, saveViews } = useShotCamera({
        cameraRef, controlsRef, initialShot: savedCameraRef.current, initialEdit: savedEditCameraRef.current,
    });
    // This dialog stays mounted across open/close, so reset to "look through the
    // camera" each time it opens (the Canvas remounts and RestoreCamera reloads
    // the shot view — the editor view would otherwise desync).
    useEffect(() => { if (open) setCamMode('shot'); /* eslint-disable-next-line */ }, [open]);
    // Free view: pose the camera object with a move/rotate gizmo.
    const [camGizmoMode, setCamGizmoMode] = useState<'translate' | 'rotate'>('translate');

    // ── Room mode — pose the piece inside a plain placeholder room (floor + walls)
    // so it can sit in a corner / against a wall / in the middle. The worker
    // reskins the grey room into a real environment while keeping the furniture.
    const [roomMode, setRoomMode] = useState(sceneState?.room?.on ?? false);
    const [roomFloorW, setRoomFloorW] = useState(sceneState?.room?.floorW ?? 6);
    const [roomFloorD, setRoomFloorD] = useState(sceneState?.room?.floorD ?? 6);
    const [roomWallH, setRoomWallH] = useState(sceneState?.room?.wallH ?? 3);
    const [roomWalls, setRoomWalls] = useState(sceneState?.room?.walls ?? { back: true, left: true, right: false });
    // Room mode is only offered for room-building recipes — force it off elsewhere
    // (e.g. Product Photography, whose presets need a transparent product cutout).
    useEffect(() => { if (!allowRoomMode && roomMode) setRoomMode(false); }, [allowRoomMode, roomMode]);
    // Entering Room mode: show the move gizmo so the piece can be positioned.
    useEffect(() => { if (roomMode) { setShowGizmo(true); setTransformMode('translate'); } }, [roomMode]);

    // ── Library props — real furniture from the shared shelf, placed beside
    // the hero. Same selection/gizmo contract as blockouts; a mesh instead of
    // a grey mass, so the capture shows the furniture itself.
    const studioApi = useContentStudioApi();
    const [sceneProps, setSceneProps] = useState<PlacedProp[]>(sceneState?.props ?? []);
    const [selPropId, setSelPropId] = useState<string | null>(null);
    const [selPropObj, setSelPropObj] = useState<THREE.Object3D | null>(null);
    // Move and rotate only. Scale left the mode switch when it stopped being
    // three-axis — it now has its own always-on handle, so there is nothing to
    // switch INTO and no mode in which a piece cannot be resized.
    const [propGizmo, setPropGizmo] = useState<'translate' | 'rotate'>('translate');
    const propRefs = useRef<Map<string, THREE.Group>>(new Map());
    const propSeq = useRef(0);
    /**
     * A different product clears the furniture around it — but not on mount.
     *
     * This effect fires on the first render too, which would throw away the
     * placement just restored from the node. The guard keeps the intent (swap
     * the product, lose a scene arranged around the old one) while letting a
     * reload come back to what was captured.
     */
    const propsMounted = useRef(false);
    useEffect(() => {
        if (!propsMounted.current) { propsMounted.current = true; return; }
        setSceneProps([]);
        setSelPropId(null);
    }, [modelPath, productUuid]);
    // Fetched when the studio opens, not when the panel does: the dock has no
    // open callback, and the list is picker metadata — small enough to warm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { openPropLibrary(); }, []);
    useEffect(() => { if (!selPropId) setSelPropObj(null); }, [selPropId]);

    /**
     * Generating a prop from the vendor's own photograph.
     *
     * The worker takes minutes, so the prop appears in the picker immediately in
     * a `generating` state and is polled until its mesh exists. Polling is not
     * only for the badge: the server finalises the prop on the first poll that
     * finds the job done, copying the GLB into the library before the job's own
     * copy expires — so a picker that never polled would leave the vendor a
     * library entry pointing at nothing.
     */
    const propPollRef = useRef<Map<string, number>>(new Map());
    const [propUploading, setPropUploading] = useState(false);
    const propFileRef = useRef<HTMLInputElement>(null);

    const pollProp = React.useCallback((propId: string) => {
        if (propPollRef.current.has(propId)) return;
        const check = async () => {
            try {
                const res = await studioApi.getStudioPropStatus(propId);
                if (res.status === 'generating') return;
                window.clearInterval(propPollRef.current.get(propId));
                propPollRef.current.delete(propId);
                setLibraryProps((l) => l.map((p) => (
                    p.propId === propId
                        ? { ...p, status: res.status, previewUrl: res.previewUrl || p.previewUrl, error: res.error }
                        : p
                )));
            } catch {
                // A failed poll is not a failed prop — the next tick retries.
            }
        };
        propPollRef.current.set(propId, window.setInterval(check, 5000));
        // Check once now rather than waiting out the first interval: a prop
        // being re-armed on load has usually finished already, and five seconds
        // of "Building…" over a mesh that exists reads as the bug this fixes.
        void check();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Timers outlive the dialog otherwise, polling for a studio nobody is in.
    useEffect(() => () => {
        propPollRef.current.forEach((t) => window.clearInterval(t));
        propPollRef.current.clear();
    }, []);

    const uploadPropImage = async (file: File) => {
        setPropUploading(true);
        try {
            const created = await studioApi.createStudioProp(file, { name: file.name });
            const placeholder: StudioProp = {
                propId: created.propId,
                name: file.name.replace(/\.[^.]+$/, ''),
                category: MY_LIBRARY,
                status: 'generating',
                mine: true,
            };
            setLibraryProps((l) => [placeholder, ...l]);
            setPropCategories((c) => (c.includes(MY_LIBRARY) ? c : [MY_LIBRARY, ...c]));
            setPropCategory(MY_LIBRARY);
            pollProp(created.propId);
        } catch (err: any) {
            setError(err?.message || 'Could not start the 3D generation');
        } finally {
            setPropUploading(false);
        }
    };

    const removeLibraryProp = async (propId: string) => {
        setLibraryProps((l) => l.filter((p) => p.propId !== propId));
        try { await studioApi.deleteStudioProp(propId); } catch { /* already gone is fine */ }
    };

    /** True while a library piece is being dragged over the scene. */
    const [dragOverScene, setDragOverScene] = useState(false);
    /** Which left panel is open, if any. Null is the bare viewport. */
    const [leftPanel, setLeftPanel] = useState<'furniture' | 'room' | null>(null);
    /** Set by a component inside the Canvas — see FloorDropResolver. */
    const floorDropRef = useRef<((x: number, y: number) => [number, number, number] | null) | null>(null);

    /**
     * Place a prop, optionally where it was dropped.
     *
     * Dropping is the natural gesture for "put that there", and clicking still
     * works for anyone who does not think to drag — both land in the same place.
     */
    const placeProp = (item: StudioProp, at?: [number, number, number]) => {
        const shot = currentShotView();
        const fallback: [number, number, number] = [
            shot ? Math.max(-3, Math.min(3, shot.target[0] + 1.2)) : 1.4,
            0,
            shot ? Math.max(-3, Math.min(3, shot.target[2])) : 0.4,
        ];
        const id = `prop-${propSeq.current++}`;
        setSceneProps((l) => [...l, {
            id, propId: item.propId, name: item.name, category: item.category,
            pos: at || fallback, rotY: 0, scale: 1,
        }]);
        setSelPropId(id);
    };

    // The library, fetched when the panel first opens. Categories come from the
    // data, so a new bucket in the collection appears here without a deploy.
    const [propCategories, setPropCategories] = useState<string[]>([]);
    const [propCategory, setPropCategory] = useState<string>('');
    const [libraryProps, setLibraryProps] = useState<StudioProp[]>([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const libraryAsked = useRef(false);
    const openPropLibrary = () => {
        if (libraryAsked.current) return;
        libraryAsked.current = true;
        setLibraryLoading(true);
        Promise.all([studioApi.listStudioPropCategories(), studioApi.listStudioProps()])
            .then(([cats, list]) => {
                /**
                 * The vendor's own shelf leads, and the rest keep the API's order.
                 *
                 * It is the bucket they made themselves and where an upload lands,
                 * so it is the one they come back to. Safe as the opening tab
                 * because the backend only reports it once they own something in
                 * it — a vendor with no pieces of their own never sees it, and it
                 * can never be a leading empty tab.
                 */
                const ordered = [
                    ...cats.filter((c) => c === MY_LIBRARY),
                    ...cats.filter((c) => c !== MY_LIBRARY),
                ];
                setPropCategories(ordered);
                setLibraryProps(list);
                if (ordered.length) setPropCategory(ordered[0]);
                /**
                 * Pick the watch back up for anything still building.
                 *
                 * Polling is not only the badge — it is what FINALISES a prop
                 * server-side, copying the finished GLB into the library before
                 * the generator's own copy expires. Only the upload call armed
                 * it, so a job that finished while the tab was shut (or the
                 * machine was off) stayed "Building…" for ever, over a mesh
                 * that had been ready for twenty minutes.
                 */
                list.filter((p) => p.status === 'generating')
                    .forEach((p) => pollProp(p.propId));
            })
            .catch(() => { libraryAsked.current = false; })
            .finally(() => setLibraryLoading(false));
    };

    const deleteProp = (id: string) => {
        setSceneProps((l) => l.filter((p) => p.id !== id));
        if (selPropId === id) { setSelPropId(null); setSelPropObj(null); }
    };
    /**
     * Delete / Backspace removes whatever is selected — prop or blockout.
     *
     * The toolbar's bin icon is the discoverable route; the key is the one a
     * vendor who has just placed the wrong sofa will press first. Guarded
     * against text inputs so typing a scene brief cannot delete furniture.
     */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
            if (selPropId) { e.preventDefault(); deleteProp(selPropId); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selPropId]);

    const commitPropTransform = () => {
        const g = selPropObj;
        if (!g || !selPropId) return;
        const sc = Math.max(PROP_SCALE_MIN, Math.min(PROP_SCALE_MAX, g.scale.x));
        setSceneProps((l) => l.map((p) => (
            p.id === selPropId ? { ...p, pos: [g.position.x, 0, g.position.z], rotY: g.rotation.y, scale: sc } : p
        )));
    };
    // Detach the gizmo when nothing is selected. The selected OBJECT itself is set
    // directly (click → e.eventObject; add → the group's ref callback) so the gizmo
    // never races the mount.


    // ── Viewport video capture (render-to-real POC). The vendor frames views and
    // saves them as keyframes — or films them with the phone — and Record flies
    // the camera through them while MediaRecorder captures the canvas → a
    // "3D viewport video" + the first frame as a still. The machinery is shared
    // with the tile studio (use-studio-takes); what is this studio's own is here.
    const takes = useStudioTakes({
        open, productUuid, rendererRef, sceneRef, cameraRef, controlsRef,
        shot: { camMode, setCamMode, toggleCamMode, shotViewRef, currentShotView },
        focalLength, setLens: setFocalLength, capturing, onCapture,
        look: {
            // CLEAN composite (same as a still Capture view): drive `capturing` so
            // the grid, grey posing backdrop, gizmos + contact shadow all drop out.
            // Video can't be transparent, so paint an OPAQUE WHITE background, and
            // ease the exposure so a white product keeps its form instead of blowing
            // out to a featureless blob.
            enter: async (renderer, scene) => {
                setCapturing(true);
                renderer.toneMappingExposure = 0.6;
                await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
                if (scene) scene.background = new THREE.Color('#ffffff');
                renderer.setClearColor('#ffffff', 1);
                renderer.autoClear = true;
            },
            exit: (renderer, scene) => {
                if (scene) scene.background = null; // the posing <color> remounts on !capturing
                renderer.setClearColor('#ffffff', 1);
                renderer.toneMappingExposure = 0.85; // restore the live-viewport exposure
                setCapturing(false);
            },
        },
        // Where the product stands, so the phone shows it the same way.
        phoneSubject: () => {
            const g = modelObjRef.current;
            return {
                model: g ? {
                    pos: [g.position.x, g.position.y, g.position.z],
                    rot: [g.rotation.x, g.rotation.y, g.rotation.z],
                    scale: [g.scale.x, g.scale.y, g.scale.z],
                } : undefined,
            };
        },
    });
    const { camPathJobRef, flying } = takes;
    const saveCameraState = () => {
        const { shot, edit } = saveViews();
        if (shot) savedCameraRef.current = shot;
        if (edit) savedEditCameraRef.current = edit;
    };
    const handleClose = () => { saveCameraState(); saveModelState(); onClose(); };

    /**
     * Insurance for a scene that arrives after the first render.
     *
     * Everything above seeds from `sceneState` at mount, which is right when
     * the project graph is already loaded — the normal case. If a node ever
     * renders before its inputs land, that seeding reads undefined and the
     * studio opens blank, which looks exactly like the bug this fixes. Applied
     * once, and never over a scene the vendor has started arranging.
     */
    const sceneRestored = useRef(!!sceneState);
    useEffect(() => {
        if (sceneRestored.current || !sceneState) return;
        sceneRestored.current = true;
        savedCameraRef.current = sceneState.shot ?? null;
        savedModelTransform.current = sceneState.model ?? null;
        if (sceneState.shot) shotViewRef.current = sceneState.shot;
        setFocalLength(sceneState.lensMm);
        setAperture(sceneState.aperture);
        setAspectRatioSel(sceneState.aspectRatio);
        setSceneProps(sceneState.props || []);
        setRoomMode(sceneState.room?.on ?? false);
        setRoomFloorW(sceneState.room?.floorW ?? 6);
        setRoomFloorD(sceneState.room?.floorD ?? 6);
        setRoomWallH(sceneState.room?.wallH ?? 3);
        setRoomWalls(sceneState.room?.walls ?? { back: true, left: true, right: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneState]);

    /**
     * The scene, as the node should remember it.
     *
     * Reads the saved refs rather than the live camera, so it must be called
     * AFTER saveCameraState/saveModelState — those already hold the logic for
     * which view counts as the shot when the editor camera is the live one.
     */
    const serializeScene = (): StudioSceneState => ({
        v: 1,
        shot: savedCameraRef.current,
        lensMm: focalLength,
        aperture,
        aspectRatio: aspectRatioSel,
        model: savedModelTransform.current,
        props: sceneProps,
        room: { on: roomMode, floorW: roomFloorW, floorD: roomFloorD, wallH: roomWallH, walls: roomWalls },
    });

    // Decrypt + load the model only while the dialog is open.
    useEffect(() => {
        if (!open) { setResolvedUrl(null); return; }
        if (!productUuid) { setResolvedUrl(modelPath); return; }
        let objectUrl: string | null = null;
        let cancelled = false;
        setError(null);
        getProductModelObjectUrl(productUuid)
            .then((u) => {
                if (cancelled) { URL.revokeObjectURL(u); return; }
                objectUrl = u;
                setResolvedUrl(u);
            })
            .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load 3D model'); });
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [open, modelPath, productUuid]);

    /**
     * Drive a capture with nobody watching.
     *
     * The wait is the whole problem: `resolvedUrl` only means the GLB has been
     * fetched, not that it has been parsed and added to the scene, and grabbing
     * the buffer before then yields an empty frame — a plausible-looking PNG of
     * nothing, which is worse than an error. So this waits for real geometry to
     * appear, then for two frames to settle, and gives up rather than capturing
     * an empty scene.
     */
    useEffect(() => {
        if (!open || !headlessAngle) return undefined;
        let cancelled = false;

        /**
         * The model's own bounding sphere.
         *
         * Framing has to be relative to it: models arrive at whatever scale and
         * origin they were exported with, so a camera placed at fixed scene
         * coordinates frames one product and stares past the next. Measured
         * from the product's group alone — the scenery is not the subject, and
         * including it framed the floor.
         */
        const modelBounds = (): { center: [number, number, number]; radius: number; flat: boolean } | null => {
            const group = modelObjRef.current;
            if (!group) return null;
            group.updateWorldMatrix(true, true);
            const box = new THREE.Box3().setFromObject(group);
            if (box.isEmpty()) return null;
            const sphere = box.getBoundingSphere(new THREE.Sphere());
            const size = box.getSize(new THREE.Vector3());
            return {
                center: [sphere.center.x, sphere.center.y, sphere.center.z],
                radius: sphere.radius,
                // A rug: barely any height for its footprint.
                flat: size.y < 0.12 * Math.max(size.x, size.z),
            };
        };

        /**
         * Has the PRODUCT loaded — not "is there anything in the scene".
         *
         * The earlier version asked the scene for any non-helper mesh, and the
         * studio always has some: the ground plane, the contact shadow. So it
         * declared the model ready the instant the dialog opened, `capturing`
         * then removed exactly those meshes, and the framing had nothing left to
         * measure. The product's own group is the only honest signal.
         */
        const hasGeometry = () => {
            const group = modelObjRef.current;
            if (!group) return false;
            let found = false;
            group.traverse((o: any) => {
                if (!found && o.isMesh && o.geometry) found = true;
            });
            return found;
        };

        (async () => {
            const deadline = Date.now() + 30_000;
            while (!cancelled && Date.now() < deadline) {
                if (rendererRef.current && cameraRef.current && hasGeometry()) break;
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 120));
            }
            if (cancelled) return;

            if (!rendererRef.current || !hasGeometry()) {
                onHeadlessDone?.('The 3D model did not finish loading in time');
                return;
            }

            // A zero-sized canvas reads back as a fully transparent image —
            // which uploads perfectly happily and looks like a successful
            // capture of nothing. Refuse rather than store an empty frame.
            const canvas = rendererRef.current.domElement;
            if (!canvas.width || !canvas.height) {
                onHeadlessDone?.('The 3D view had no size to capture from');
                return;
            }

            // Put the scene into its capture state — `capturing` removes the
            // grid, gizmo and ground shadow — and give React two frames to
            // commit it, so the pose below is worked out against the scene the
            // capture will actually render.
            setCapturing(true);
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            if (cancelled) { setCapturing(false); return; }

            // Framed only now: the model has to be loaded before its size is
            // known, and the scenery gone before that size means anything.
            const bounds = modelBounds();
            if (!bounds) {
                // Guessing here is how empty frames got uploaded: a default
                // framing against an unknown model points the camera at nothing,
                // and the resulting image is valid, transparent and useless.
                setCapturing(false);
                onHeadlessDone?.('Could not work out how to frame the model');
                return;
            }
            // Framed through the lens the shot is actually taken on. The camera
            // has already resolved the focal length into a field of view, so ask
            // it rather than re-deriving one — the two drifting apart is how a
            // "correct" angle still came back cropped on two sides.
            const camNow = cameraRef.current as any;
            // A flat piece seen from a few degrees up is a sliver: every rug shot
            // "at eye level" came back as a thin pink band far away (matrix,
            // 11 Oct 2026). A person standing over a rug looks DOWN at it, so a
            // flat product is never framed below 30 degrees.
            const view = resolveNamedCameraAngle(headlessAngle, bounds.center, bounds.radius,
                camNow?.isPerspectiveCamera ? { fovDeg: camNow.fov, aspect: camNow.aspect } : undefined,
                flatProductElevation(headlessAngle, bounds.flat));

            if (view) {
                const cam = cameraRef.current as any;
                const controls = controlsRef.current;
                cam.position.set(view.pos[0], view.pos[1], view.pos[2]);
                if (controls) {
                    // The orbit limits are guard rails for a vendor dragging the
                    // view — they keep a hand-posed shot in the range Flux can
                    // build a room around. Applied to a named angle they don't
                    // guard anything, they silently answer a different question:
                    // `top-down` is outside the polar range, so it would clamp to
                    // a mid-height shot and the node would claim a flat-lay it
                    // never took. The named angle is the request; let it through.
                    if (!orbitLimitsRef.current) {
                        orbitLimitsRef.current = {
                            minPolarAngle: controls.minPolarAngle,
                            maxPolarAngle: controls.maxPolarAngle,
                            minDistance: controls.minDistance,
                            maxDistance: controls.maxDistance,
                        };
                    }
                    controls.minPolarAngle = 0;
                    controls.maxPolarAngle = Math.PI;
                    controls.minDistance = 0;
                    controls.maxDistance = Infinity;
                    controls.target.set(view.target[0], view.target[1], view.target[2]);
                    controls.update();
                } else {
                    cam.lookAt(new THREE.Vector3(view.target[0], view.target[1], view.target[2]));
                }
                cam.updateMatrixWorld(true);

            }

            // Let the pose and any lazily-loaded material settle before reading
            // the buffer.
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            if (cancelled) return;

            try {
                await captureAndUse();
                onHeadlessDone?.();
            } catch (err: any) {
                onHeadlessDone?.(err?.message || 'Capture failed');
            } finally {
                const limits = orbitLimitsRef.current;
                const controls = controlsRef.current;
                if (limits && controls) Object.assign(controls, limits);
                orbitLimitsRef.current = null;
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, headlessAngle]);

    /**
     * Fit the product's real outline, not its bounding sphere.
     *
     * The sphere distance left a rug at a low angle as a far-away sliver and a
     * long sofa small in a 4:5 frame (posed-camera harness, 11 Oct 2026): a
     * flat or wide piece fills only a fraction of the sphere it sits in.
     * Project the box corners and walk the camera along its view line until the
     * outline fills the frame; "close-detail" overfills it on purpose, so it
     * crops. The fitted view is written back as the shot, so a re-frame and the
     * stored camera agree with the picture.
     */
    const fitPoseToOutline = (cam: any, angle: string) => {
        const group = modelObjRef.current;
        if (!group || !cam?.isPerspectiveCamera) return;
        const box = new THREE.Box3().setFromObject(group);
        if (box.isEmpty()) return;
        const corners: THREE.Vector3[] = [];
        for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) corners.push(new THREE.Vector3(x, y, z));
        const forward = new THREE.Vector3();
        cam.getWorldDirection(forward);
        const center = box.getCenter(new THREE.Vector3());
        // Pivot: where the view line passes the product's centre.
        const toCenter = center.clone().sub(cam.position);
        const pivot = cam.position.clone().add(forward.clone().multiplyScalar(Math.max(0.05, toCenter.dot(forward))));
        const fill = angle === 'close-detail' ? 1.35 : 0.84;
        // Flat = a rug: seen low it is a thin band however wide it is, so it
        // is fitted by its height, the way a photographer kneels close to a
        // rug rather than shooting a strip.
        const size = box.getSize(new THREE.Vector3());
        const flat = size.y < 0.12 * Math.max(size.x, size.z);
        // How full the frame is at a given distance (1 = on target). Perspective
        // makes this steeply non-linear up close, so a ratio step overshoots and
        // oscillates (a rug at a low angle bounced between a sliver and a crop);
        // the distance is bisected instead. Fullness only grows as it nears.
        const offset = cam.position.clone().sub(pivot);
        const measure = (scale: number): [number, number] | null => {
            cam.position.copy(pivot.clone().add(offset.clone().multiplyScalar(scale)));
            cam.updateMatrixWorld(true);
            let extX = 0;
            let extY = 0;
            for (const c of corners) {
                const v = c.clone().project(cam);
                if (v.z > 1 || v.z < -1) return null;
                extX = Math.max(extX, Math.abs(v.x));
                extY = Math.max(extY, Math.abs(v.y));
            }
            return [extX, extY];
        };
        // A flat piece seen low (a thin band) fills by its height, the sides
        // allowed to crop; seen from above it fits like anything else.
        const start = measure(1);
        const band = flat && !!start && start[1] < 0.35 * start[0];
        const fullness = (scale: number): number => {
            const m = measure(scale);
            if (!m) return Infinity;
            return band ? Math.max(m[1] / 0.4, m[0] / 2) : Math.max(m[0], m[1]) / fill;
        };
        let near = 0.04;
        let far = 1;
        while (fullness(far) > 1 && far < 64) far *= 2;
        for (let k = 0; k < 28; k += 1) {
            const mid = Math.sqrt(near * far);
            if (fullness(mid) > 1) near = mid; else far = mid;
        }
        fullness(far);
        cam.updateMatrixWorld(true);
        shotViewRef.current = { pos: cam.position.toArray() as [number, number, number], target: pivot.toArray() as [number, number, number] };
    };

    const captureAndUse = async () => {
        const renderer = rendererRef.current;
        if (!renderer) return;
        setSaving(true);
        setError(null);
        const hiddenHelpers: THREE.Object3D[] = [];
        // Free view: capture from the SHOT camera, not the editor view.
        let restoreCam: (() => void) | null = null;
        try {
            // Hide the gizmo/grid/shadow + bypass post-processing (via `capturing`).
            setCapturing(true);
            // Wait for React to commit — so the grid/gizmo/shadow are actually
            // removed from the scene graph — then FORCE a plain transparent render
            // directly (the post-processing loop is unreliable to grab from, and it
            // can't output alpha). This writes a clean product-only cutout into the
            // preserved drawing buffer, which we then read with toBlob.
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            const scene = sceneRef.current;
            const camera = cameraRef.current;
            restoreCam = captureFromShot(camera, focalLength);
            // A named (headless) pose is fitted here, on the camera that actually
            // takes the picture — its lens and aspect are only final now.
            if (headlessAngle && camera) fitPoseToOutline(camera as any, headlessAngle);
            if (scene && camera) {
                // Belt-and-suspenders: hide any tagged helper (camera object, etc.)
                // still in the graph so React's unmount can't race the render.
                hiddenHelpers.push(...hideCaptureHelpers(scene));
                // Product mode → transparent cutout (worker builds a room around it).
                // Room mode → OPAQUE render of the placeholder room + piece, so the
                // worker reskins the grey room while keeping the furniture + placement.
                renderer.autoClear = true;
                if (roomMode) {
                    scene.background = null;
                    renderer.setClearColor('#ffffff', 1);
                } else {
                    scene.background = null; // transparent (drop any studio colour)
                    renderer.setClearColor('#ffffff', 0); // clearAlpha 0 → transparent
                }
                renderer.clear();
                renderer.render(scene, camera);
                // Look at what actually landed before uploading it. An empty
                // frame is a valid PNG: it uploads, it stores, and the node then
                // carries a snapshot of nothing that only shows up as a bad
                // generation much later. Cheaper to refuse it here.
                if (!bufferHasSubject(renderer, roomMode)) {
                    throw new Error('The 3D view rendered an empty frame — nothing was in shot');
                }
            }
            const blob: Blob | null = await new Promise((resolve) => {
                renderer.domElement.toBlob((b) => resolve(b), 'image/png', 0.95);
            });
            if (!blob) throw new Error('Canvas toBlob returned null');
            const file = new File([blob], `studio3d-${Date.now()}.png`, { type: 'image/png' });
            const url = await onCapture(file);
            onSnapshotUpdated(url, shotCameraViewText(), { lensMm: focalLength, aperture, aspectRatio: aspectRatioSel }, [], roomMode,
                describeSceneProps(sceneProps),
                headlessAngle || null);
            saveCameraState();
            saveModelState();
            // Stored with the frame, from the same moment, so the scene the
            // studio reopens to is the one that produced the snapshot.
            onSceneStateChange?.(serializeScene());
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Failed to capture view');
            // A headless capture has nobody to read that error banner — the
            // dialog closes itself and the run reports back instead. Swallowing
            // it here is what let a failed capture be announced as a finished
            // one, so it goes up to the caller.
            if (headlessAngle) throw err;
        } finally {
            restoreCaptureHelpers(hiddenHelpers);
            if (restoreCam) restoreCam();
            // Restore the opaque clear so the live viewport paints normally again.
            renderer.setClearColor('#ffffff', 1);
            setCapturing(false);
            setSaving(false);
        }
    };

    const aspect = aspectToNumber(aspectRatioSel);

    // ── Bottom dock — every tool lives in the floating action bar; clicking an
    // icon opens its compact options panel above the bar (StudioDock).
    const { t } = useTranslation();
    const backdropLabel = (v: string) => (v === '#EDEFF2'
        ? t('CONTENT_STUDIO.STUDIO_BACKDROP_LIGHT', 'Light')
        : v === '#C0C5CE'
            ? t('CONTENT_STUDIO.STUDIO_BACKDROP_MID', 'Mid')
            : t('CONTENT_STUDIO.STUDIO_BACKDROP_DARK', 'Dark'));
    const dockItems: StudioDockItemDef[] = [
        {
            key: 'frame',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_FRAME', 'Frame'),
            value: aspectRatioSel,
            icon: <Ratio />,
            panelWidth: 260,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_FRAME', 'Frame')} value={aspectRatioSel} />
                    <div className="grid grid-cols-3 gap-1.5">
                        {ASPECT_PRESETS.map((a) => (
                            <StudioPill key={a.v} active={aspectRatioSel === a.v} onClick={() => { setAspectRatioSel(a.v); onAspectRatioChange?.(a.v); }}>{a.label}</StudioPill>
                        ))}
                    </div>
                </div>
            ),
        },
        {
            key: 'lens',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_LENS', 'Lens'),
            value: `${focalLength}mm`,
            icon: <Camera />,
            panelWidth: 320,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_LENS', 'Lens')} value={`${focalLength}mm · ${lensCharacter(focalLength)}`} />
                    <div className="mb-1 grid grid-cols-3 gap-1.5">
                        {LENS_PRESETS.map((p) => (
                            <StudioPill key={p.mm} active={focalLength === p.mm} onClick={() => setFocalLength(p.mm)}>{p.mm}mm</StudioPill>
                        ))}
                    </div>
                    <StudioMarkSlider value={focalLength} min={14} max={200} onChange={setFocalLength} marks={LENS_PRESETS.map((p) => ({ value: p.mm }))} />
                </div>
            ),
        },
        {
            key: 'depth',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_DEPTH', 'Depth'),
            value: `f/${aperture}`,
            icon: <Aperture />,
            panelWidth: 320,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_DOCK_DEPTH', 'Depth of field')} value={`f/${aperture}`} />
                    <div className="mb-1 grid grid-cols-4 gap-1.5">
                        {APERTURE_STOPS.map((f) => (
                            <StudioPill key={f} active={aperture === f} onClick={() => setAperture(f)}>f/{f}</StudioPill>
                        ))}
                    </div>
                    <StudioMarkSlider
                        value={Math.max(0, APERTURE_STOPS.indexOf(aperture))}
                        min={0}
                        max={APERTURE_STOPS.length - 1}
                        discrete
                        marks={APERTURE_STOPS.map((_, i) => ({ value: i }))}
                        onChange={(i) => setAperture(APERTURE_STOPS[i])}
                    />
                    <p className="text-xs leading-normal text-muted-foreground">
                        {apertureCharacter(aperture)} · click the product to set focus.
                    </p>
                </div>
            ),
        },
        {
            key: 'backdrop',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_BACKDROP', 'Backdrop'),
            value: backdropLabel(previewBg),
            icon: <Wallpaper />,
            panelWidth: 260,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_PREVIEW_BACKDROP', 'Preview backdrop')} value={backdropLabel(previewBg)} />
                    <div className="flex items-center gap-2">
                        {BACKDROP_PRESETS.map((b) => (
                            <button
                                key={b.v}
                                onClick={() => setPreviewBg(b.v)}
                                title={backdropLabel(b.v)}
                                className={cn(
                                    'size-7 cursor-pointer rounded-full p-0 transition-shadow',
                                    previewBg === b.v
                                        ? 'border-2 border-foreground ring-1 ring-foreground/40 ring-offset-2'
                                        : 'border',
                                )}
                                // Swatch colours are data — the posing backdrops themselves.
                                style={{ backgroundColor: b.v }}
                            />
                        ))}
                    </div>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                        {t('CONTENT_STUDIO.STUDIO_BACKDROP_HINT', 'Posing only — never part of the capture.')}
                    </p>
                </div>
            ),
        },
        ...takeDockItems(t, takes, { onTakeRecorded, onClose }),
        ...(referenceImageUrl ? [{
            key: 'guide',
            label: t('CONTENT_STUDIO.STUDIO_DOCK_GUIDE', 'Guide'),
            value: `${Math.round(refOpacity * 100)}%`,
            icon: <ImageIcon />,
            panelWidth: 280,
            panel: (
                <div>
                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_REFERENCE_GUIDE', 'Reference guide')} value={`${Math.round(refOpacity * 100)}%`} />
                    <input type="range" min={0} max={100} value={Math.round(refOpacity * 100)} onChange={(e) => setRefOpacity(Number(e.target.value) / 100)} className="w-full cursor-pointer accent-foreground" />
                </div>
            ),
        }] : []),
    ];

    return (
        <Dialog
            open={open}
            // handleClose on ANY dismissal (Escape, outside click, the X) so the
            // framing is saved however the studio is left.
            onOpenChange={(o) => { if (!o) handleClose(); }}
        >
            <DialogContent
                showCloseButton={false}
                // A headless capture opens the studio for real rather than hiding it
                // off-screen: WebGL needs the canvas laid out and drawn, and an
                // invisible one is not reliably either. It is also the honest thing
                // to show — the vendor watches the studio open, frame itself and
                // close, which is exactly what happened. Untouchable while it runs
                // so a stray click cannot move the camera mid-capture.
                onEscapeKeyDown={(e) => { if (headlessAngle) e.preventDefault(); }}
                onInteractOutside={(e) => { if (headlessAngle) e.preventDefault(); }}
                // No dimming sheet. The studio is a workspace, not an alert — and the
                // canvas behind it is the vendor's own project, which they should be
                // able to see they are still inside.
                overlayClassName="bg-black/20 backdrop-blur-[2px]"
                className={cn(
                    // Wider: the furniture library lives inside the dialog now,
                    // beside the viewport rather than in a popover above the bar.
                    'block w-[calc(100%-2rem)] max-w-[1320px] gap-0 overflow-hidden p-0 sm:max-w-[1320px]',
                    headlessAngle && 'pointer-events-none',
                )}
            >
            <DialogTitle className="sr-only">{t('CONTENT_STUDIO.STUDIO_FURNITURE_TITLE', 'Product studio')}</DialogTitle>
            <div
                // The Dialog is portaled, but React still bubbles its events
                // through the React tree to the RecipeNode behind it — which would
                // drag the node. Isolate the whole dialog from those interactions.
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                // Right-click bubbles through the portal to the canvas behind,
                // which would open the canvas context menu over the studio.
                onContextMenu={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="relative flex h-[78vh] max-h-[720px] bg-muted"
            >
                {/* Left rail — the two things that BUILD the scene, kept apart
                    from the camera settings in the bar below. Everything here is
                    a place to put something; everything in the bar is a property
                    of the shot. */}
                <div className="z-[7] flex w-[68px] shrink-0 flex-col items-center gap-1 border-e bg-background py-3">
                    <StudioRailButton
                        active={leftPanel === 'furniture'}
                        onClick={() => setLeftPanel((p) => (p === 'furniture' ? null : 'furniture'))}
                        icon={<Sofa />}
                        label={t('CONTENT_STUDIO.STUDIO_DOCK_PROPS', 'Props')}
                        badge={sceneProps.length || undefined}
                    />
                    {allowRoomMode && (
                        <StudioRailButton
                            active={leftPanel === 'room'}
                            onClick={() => setLeftPanel((p) => (p === 'room' ? null : 'room'))}
                            icon={<DoorOpen />}
                            label={t('CONTENT_STUDIO.STUDIO_DOCK_ROOM', 'Room')}
                            dot={roomMode}
                        />
                    )}
                </div>

                {/* Left panel — the library, two columns, beside the scene rather
                    than in a popover over it: placing furniture is a
                    browse-and-drag job, not a menu pick. */}
                {leftPanel && (
                    <div className="z-[7] flex w-[292px] shrink-0 flex-col border-e bg-background">
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <span className="text-[13px] font-medium text-foreground">
                                {leftPanel === 'furniture'
                                    ? t('CONTENT_STUDIO.STUDIO_PROPS', 'Furniture library')
                                    : t('CONTENT_STUDIO.STUDIO_ROOM_MODE', 'Room')}
                            </span>
                            <button
                                onClick={() => setLeftPanel(null)}
                                aria-label={t('CONTENT_STUDIO.STUDIO_CLOSE', 'Close')}
                                className="cursor-pointer border-0 bg-transparent leading-none text-muted-foreground hover:text-foreground"
                            >
                                <X className="size-[18px]" />
                            </button>
                        </div>

                        {leftPanel === 'room' && (
                            <div className="overflow-y-auto p-4">
                                <div>
                                    <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_ROOM_MODE', 'Room')} value={roomMode ? t('CONTENT_STUDIO.STUDIO_ROOM_ON', 'On') : t('CONTENT_STUDIO.STUDIO_ROOM_OFF', 'Off')} />
                                    <div className={cn('grid grid-cols-2 gap-1.5', roomMode && 'mb-3')}>
                                        <StudioPill active={!roomMode} onClick={() => setRoomMode(false)}>{t('CONTENT_STUDIO.STUDIO_PRODUCT_ONLY', 'Product only')}</StudioPill>
                                        <StudioPill active={roomMode} onClick={() => setRoomMode(true)}>{t('CONTENT_STUDIO.STUDIO_IN_ROOM', 'In a room')}</StudioPill>
                                    </div>
                                    <div className={cn('text-[11px] leading-snug text-muted-foreground', roomMode && 'mb-3')}>
                                        {roomMode
                                            ? t('CONTENT_STUDIO.STUDIO_ROOM_HINT_ON', 'Pose the piece in the placeholder room (corner, against a wall, centre) with the move gizmo. The worker paints a real room around it, keeping the furniture.')
                                            : t('CONTENT_STUDIO.STUDIO_ROOM_HINT_OFF', 'Product only — the piece is captured on its own and the AI builds a room around it.')}
                                    </div>
                                    {roomMode && (
                                        <>
                                            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_WALLS', 'Walls')} value={[roomWalls.back && t('CONTENT_STUDIO.STUDIO_BACK', 'Back'), roomWalls.left && t('CONTENT_STUDIO.STUDIO_LEFT', 'Left'), roomWalls.right && t('CONTENT_STUDIO.STUDIO_RIGHT', 'Right')].filter(Boolean).join(' · ') || t('CONTENT_STUDIO.STUDIO_NONE', 'None')} />
                                            <div className="mb-3 grid grid-cols-3 gap-1.5">
                                                <StudioPill active={roomWalls.back} onClick={() => setRoomWalls((w) => ({ ...w, back: !w.back }))}>{t('CONTENT_STUDIO.STUDIO_BACK', 'Back')}</StudioPill>
                                                <StudioPill active={roomWalls.left} onClick={() => setRoomWalls((w) => ({ ...w, left: !w.left }))}>{t('CONTENT_STUDIO.STUDIO_LEFT', 'Left')}</StudioPill>
                                                <StudioPill active={roomWalls.right} onClick={() => setRoomWalls((w) => ({ ...w, right: !w.right }))}>{t('CONTENT_STUDIO.STUDIO_RIGHT', 'Right')}</StudioPill>
                                            </div>
                                            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_ROOM_SIZE', 'Room size')} value={`${roomFloorW}×${roomFloorD}m`} />
                                            <StudioMarkSlider value={roomFloorW} min={3} max={14} marks={[4, 6, 8, 10, 12].map((v) => ({ value: v }))} onChange={(v) => setRoomFloorW(Math.round(v))} />
                                            <StudioMarkSlider value={roomFloorD} min={3} max={14} marks={[4, 6, 8, 10, 12].map((v) => ({ value: v }))} onChange={(v) => setRoomFloorD(Math.round(v))} />
                                            <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_WALL_HEIGHT', 'Wall height')} value={`${roomWallH}m`} />
                                            <StudioMarkSlider value={roomWallH} min={2} max={5} marks={[2, 3, 4, 5].map((v) => ({ value: v }))} onChange={(v) => setRoomWallH(Math.round(v))} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {leftPanel === 'furniture' && (
                            <>
                                {/* Make one from a photograph. The vendor's own
                                    pieces are private and land in My library. */}
                                <div className="px-3 pt-2.5">
                                    <input
                                        ref={propFileRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            e.target.value = '';
                                            if (f) void uploadPropImage(f);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        disabled={propUploading}
                                        onClick={() => propFileRef.current?.click()}
                                        className={cn(
                                            'flex w-full items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed bg-muted py-2 text-xs text-muted-foreground transition-colors hover:border-input hover:text-foreground',
                                            propUploading ? 'cursor-wait' : 'cursor-pointer',
                                        )}
                                    >
                                        <ImagePlus className="size-4" />
                                        {propUploading
                                            ? t('CONTENT_STUDIO.STUDIO_PROP_UPLOADING', 'Sending…')
                                            : t('CONTENT_STUDIO.STUDIO_PROP_FROM_IMAGE', 'Make one from a photo')}
                                    </button>
                                </div>

                                {/* Categories, from the data — a new bucket in the
                                    collection appears here without a deploy. */}
                                <div className="flex flex-wrap gap-1 border-b px-3 py-2.5">
                                    {propCategories.map((cat) => (
                                        <StudioPill key={cat} active={propCategory === cat} onClick={() => setPropCategory(cat)}>
                                            {titleCase(cat)}
                                        </StudioPill>
                                    ))}
                                </div>

                                <div className="flex-1 overflow-y-auto p-3">
                                    {libraryLoading && (
                                        <div className="py-6 text-center text-xs text-muted-foreground">
                                            {t('CONTENT_STUDIO.STUDIO_PROPS_LOADING', 'Loading the library…')}
                                        </div>
                                    )}
                                    {!libraryLoading && libraryProps.filter((p) => p.category === propCategory).length === 0 && (
                                        <div className="py-6 text-center text-xs text-muted-foreground">
                                            {t('CONTENT_STUDIO.STUDIO_PROPS_EMPTY', 'Nothing in this category yet.')}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                        {libraryProps.filter((p) => p.category === propCategory).map((item) => (
                                            <div
                                                key={item.propId}
                                                // Nothing to put in the scene until
                                                // the mesh exists.
                                                draggable={item.status !== 'generating' && item.status !== 'failed'}
                                                onDragStart={(e: React.DragEvent) => {
                                                    e.dataTransfer.setData('application/x-studio-prop', item.propId);
                                                    e.dataTransfer.effectAllowed = 'copy';
                                                }}
                                                onClick={() => {
                                                    if (item.status === 'generating' || item.status === 'failed') return;
                                                    placeProp(item);
                                                }}
                                                title={item.status === 'failed' ? (item.error || 'Generation failed') : item.name}
                                                className="cursor-grab overflow-hidden rounded-lg border bg-background transition-colors hover:border-input active:cursor-grabbing"
                                            >
                                                {item.status === 'generating' ? (
                                                    <div className="flex h-24 w-full flex-col items-center justify-center gap-1 bg-accent text-muted-foreground">
                                                        <Loader2 className="size-[18px] animate-spin" />
                                                        <span className="text-[10px]">{t('CONTENT_STUDIO.STUDIO_PROP_BUILDING', 'Building…')}</span>
                                                    </div>
                                                ) : item.status === 'failed' ? (
                                                    <div className="flex h-24 w-full items-center justify-center bg-destructive/10 px-2 text-center text-[10px] text-destructive">
                                                        {t('CONTENT_STUDIO.STUDIO_PROP_FAILED', 'Could not build this one')}
                                                    </div>
                                                ) : item.previewUrl ? (
                                                    <img src={item.previewUrl} alt={item.name} loading="lazy" draggable={false} className="block h-24 w-full bg-muted object-cover" />
                                                ) : (
                                                    <div className="h-24 w-full bg-accent" />
                                                )}
                                                <div className="flex items-center gap-0.5 px-1.5 py-1">
                                                    <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                                                        {item.name}
                                                    </span>
                                                    {item.mine && (
                                                        <button
                                                            type="button"
                                                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); void removeLibraryProp(item.propId); }}
                                                            title={t('CONTENT_STUDIO.STUDIO_REMOVE', 'Remove') as string}
                                                            className="cursor-pointer border-0 bg-transparent p-0 leading-none text-muted-foreground hover:text-destructive"
                                                        >
                                                            <Trash2 className="size-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {sceneProps.length > 0 && (
                                    <div className="max-h-[168px] overflow-y-auto border-t p-3">
                                        <StudioSectionHeader label={t('CONTENT_STUDIO.STUDIO_PROPS_PLACED', 'In the scene')} value={`${sceneProps.length}`} />
                                        {sceneProps.map((p) => (
                                            <div key={p.id} className="flex items-center gap-1.5 py-1">
                                                <button
                                                    onClick={() => { setSelPropId(p.id); const g = propRefs.current.get(p.id); if (g) setSelPropObj(g); }}
                                                    className={cn(
                                                        'min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent p-0 text-start text-xs',
                                                        selPropId === p.id ? 'font-medium text-foreground' : 'text-foreground',
                                                    )}
                                                >
                                                    {p.name}
                                                </button>
                                                <button
                                                    onClick={() => deleteProp(p.id)}
                                                    className="cursor-pointer border-0 bg-transparent leading-none text-destructive"
                                                >
                                                    <Trash2 className="size-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Scene column */}
                <div className="relative min-w-0 flex-1">
                {/* Full-bleed viewport — the frame floats centred, clear of the dock.
                    containerType:size lets the frame size itself against BOTH the
                    available width and height (cqw/cqh) so any aspect ratio is
                    letterbox-contained correctly. */}
                <div className="absolute inset-0 flex items-center justify-center px-4 pt-4 pb-24 [container-type:size]">
                    <div
                        onWheel={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        // Drag a piece out of the library and drop it where it
                        // should stand. The drop point is raycast onto the floor
                        // plane through the live camera, so it lands where the
                        // vendor aimed rather than at a guessed offset.
                        onDragOver={(e: React.DragEvent) => {
                            if (!e.dataTransfer.types.includes('application/x-studio-prop')) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                            if (!dragOverScene) setDragOverScene(true);
                        }}
                        onDragLeave={() => setDragOverScene(false)}
                        onDrop={(e: React.DragEvent) => {
                            const propId = e.dataTransfer.getData('application/x-studio-prop');
                            setDragOverScene(false);
                            if (!propId) return;
                            e.preventDefault();
                            const item = libraryProps.find((p) => p.propId === propId);
                            if (!item) return;
                            // No resolver means the canvas has not mounted its
                            // camera yet; the click path's default placement is
                            // still better than dropping the piece on the floor
                            // at the origin, or nowhere.
                            placeProp(item, floorDropRef.current?.(e.clientX, e.clientY) || undefined);
                        }}
                        className={cn(
                            'relative max-h-full max-w-full overflow-hidden bg-muted',
                            dragOverScene && 'outline-2 -outline-offset-[3px] outline-foreground outline-dashed',
                        )}
                        // Fit within both dimensions: width is the smaller of the full
                        // container width and (container height × aspect), so a square
                        // stays square in a landscape container instead of overflowing.
                        style={{ aspectRatio: `${aspect}`, width: `min(100cqw, calc(100cqh * ${aspect}))` }}
                    >
                    {/* The GLB loader throws from inside Suspense when a model
                        fails to parse, and without a boundary here that error
                        escapes to the app-level one — a single bad model blanks
                        the entire page instead of this dialog. Contain it. */}
                    <ErrorBoundary fallback={<Studio3DLoadFallback />}>
                    <Canvas
                        // A click that hits nothing clears the selection — the
                        // background is the one thing that is always clickable,
                        // so it is the universal way out of a selection.
                        onPointerMissed={() => setSelPropId(null)}
                        // Opaque WHITE studio sweep so real depth-of-field renders
                        // reliably (post-processing + transparency don't mix well) and
                        // the captured frame IS the final studio look (WYSIWYG).
                        // dpr [2,3] renders the backing store at ≥2× so the captured
                        // frame is crisp (was 1× → ~858px, soft when upscaled to 1024).
                        // The capture is taken from this same canvas, so the
                        // backing store IS the output resolution — there is no
                        // separate high-res render pass to fall back on.
                        dpr={[2, 3]}
                        shadows
                        // alpha: true so the capture can be a TRANSPARENT cutout — the
                        // worker keys the product off the alpha channel (not fragile
                        // near-white detection), so white/black/any-colour products
                        // separate cleanly from the background.
                        gl={{ preserveDrawingBuffer: true, antialias: true, alpha: true }}
                        camera={{ position: [3.5, 2.2, 4], fov: 35 }}
                        onCreated={({ gl, scene, camera }) => {
                            rendererRef.current = gl;
                            sceneRef.current = scene;
                            cameraRef.current = camera;
                            gl.setClearColor('#ffffff', 1);
                            // Slightly under 1.0 for pleasant studio form (highlights not
                            // blown flat). Product/background separation is handled by the
                            // alpha cutout now, not by dimming the product to grey.
                            gl.toneMappingExposure = 0.85;
                        }}
                    >
                        {/* Posing backdrop (cosmetic — pick light/dark below to see the
                            product, e.g. a white piece against dark). During CAPTURE we
                            drop it entirely so the frame is TRANSPARENT: the product is
                            grabbed as an alpha cutout the worker keys reliably. */}
                        {!capturing && <color attach="background" args={[previewBg]} />}
                        <FloorDropResolver resolverRef={floorDropRef} />
                        <CameraRig focalLength={camMode === 'edit' ? EDITOR_FOCAL_LENGTH : focalLength} />
                        <CameraPathAnimator jobRef={camPathJobRef} />
                        <ReleaseContextOnUnmount />
                        <RestoreCamera savedRef={savedCameraRef} controlsRef={controlsRef} />
                        <ambientLight intensity={0.3} />
                        {/* KEY light — strong and directional so WHITE products develop a
                            clear light-to-shadow gradient (form) instead of washing out
                            flat. Casts real shadows so shelf cavities / undersides read as
                            depth even against the plain white capture sweep, where an
                            evenly-lit white product would otherwise dissolve into the
                            background and lose all its detail. */}
                        <directionalLight
                            position={[4, 7, 3]}
                            intensity={1.5}
                            castShadow
                            // 4096 over a wider frustum is still denser than 2048
                            // over the old one (341 vs 256 texels per unit), and
                            // the frustum has to be wider: props can be dropped
                            // out to ±6, and anything outside the shadow camera
                            // simply stops casting — furniture standing in the
                            // scene with no shadow under it reads as pasted on,
                            // which is the exact failure the render is fighting.
                            shadow-mapSize-width={4096}
                            shadow-mapSize-height={4096}
                            shadow-camera-near={0.5}
                            shadow-camera-far={30}
                            shadow-camera-left={-7}
                            shadow-camera-right={7}
                            shadow-camera-top={7}
                            shadow-camera-bottom={-7}
                            shadow-bias={-0.0004}
                            shadow-normalBias={0.02}
                        />
                        {/* Soft opposite-side fill so the shadowed side keeps detail
                            (form, not crushed black). */}
                        <directionalLight position={[-5, 3, -2]} intensity={0.35} />
                        <Suspense fallback={null}>
                            {/* Self-contained studio softbox rig (no HDRI/CDN) — gives
                                image-based lighting + reflections so PBR materials read
                                realistically. Kept as gentle FILL (the directional key
                                above does the shaping) so glossy GLB materials don't blow
                                out and white products don't flatten into even wash. */}
                            {/* 512, not 256. With one hero on a white sweep the
                                low-res environment was invisible; a scene full of
                                library furniture reflects it in every glossy
                                surface, and at 256 those reflections read as
                                blocky smears. Still baked once (frames=1), so the
                                cost is one extra render at open, not per frame. */}
                            <Environment resolution={512} frames={1}>
                                <Lightformer form="rect" intensity={0.75} position={[0, 4, 3]} rotation={[-Math.PI / 3, 0, 0]} scale={[9, 5, 1]} />
                                <Lightformer form="rect" intensity={0.34} position={[-5, 2, 1]} rotation={[0, Math.PI / 4, 0]} scale={[4, 6, 1]} />
                                <Lightformer form="rect" intensity={0.34} position={[5, 2, 1]} rotation={[0, -Math.PI / 4, 0]} scale={[4, 6, 1]} />
                                <Lightformer form="rect" intensity={0.45} position={[0, 3, -5]} rotation={[Math.PI / 4, 0, 0]} scale={[7, 3, 1]} />
                            </Environment>
                            {/* Clicking the hero reclaims the selection: with a
                                prop or blockout selected, the hero's own gizmo
                                stays hidden until they are deselected — and
                                nothing else deselects them, so one placed sofa
                                used to lock the hero out for good. */}
                            {/* Clicking the hero SELECTS it, the way clicking a
                                prop selects the prop: gizmo on, other selections
                                off, focus point set. The gizmo used to hide
                                behind the top-right "Show controls" toggle, so
                                the one object every scene has was the only one
                                you could not just click and drag. */}
                            {resolvedUrl && <StudioModel url={resolvedUrl} onPick={(p) => { setFocusTarget(p); setSelPropId(null); setShowGizmo(true); }} groupRef={handleModelRef} layFlat={isRug} />}
                            {/* ROOM MODE — a plain placeholder room (tan floor + grey
                                walls) the piece is posed inside; kept in the capture and
                                reskinned by the worker into a real environment. */}
                            {roomMode && <PlaceholderRoom floorW={roomFloorW} floorD={roomFloorD} wallH={roomWallH} walls={roomWalls} />}
                            {/* Very faint grounding shadow — barely-there contact only.
                                Hidden during capture so the cutout is product-only (each
                                pipeline path adds its own realistic contact shadow). */}
                            {!capturing && <ContactShadows position={[0, 0, 0]} opacity={0.12} scale={9} blur={3.2} far={4} resolution={1024} color="#1A1A1A" />}
                            {/* Ground floor + grid — spatial context for posing (Product
                                mode only; Room mode brings its own floor). Ground plane
                                stays while RECORDING (the model needs a floor); grid lines
                                drop so they don't read as a floor pattern. */}
                            {!roomMode && !capturing && (showGrid || flying) && (
                                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.005, 0]}>
                                    <planeGeometry args={[80, 80]} />
                                    <meshStandardMaterial color="#AEB3BC" roughness={1} metalness={0} />
                                </mesh>
                            )}
                            {!roomMode && !capturing && showGrid && !flying && (
                                <Grid
                                    position={[0, 0.002, 0]}
                                    infiniteGrid
                                    cellSize={0.5}
                                    cellThickness={0.6}
                                    cellColor="#9096A0"
                                    sectionSize={2.5}
                                    sectionThickness={1}
                                    sectionColor="#6E7480"
                                    fadeDistance={24}
                                    fadeStrength={1.3}
                                    followCamera={false}
                                />
                            )}
                        </Suspense>
                        {/* In-canvas gizmo — drag the rings to rotate (or arrows to
                            move) the object in place. Auto-disables OrbitControls
                            while dragging (OrbitControls is makeDefault). */}
                        {modelObj && !capturing && !flying && showGizmo && camMode === 'shot' && !selPropId && (
                            <TransformControls object={modelObj} mode={transformMode} size={0.7} />
                        )}
                        {/* Library props — real furniture from the shared shelf.
                            Captured as pixels; the typed list rides the capture
                            so the prompt can name each one as content to keep. */}
                        {sceneProps.map((p) => (
                            <ScenePropObject
                                key={p.id}
                                prop={p}
                                selected={selPropId === p.id}
                                capturing={capturing}
                                groupRef={(g) => {
                                    if (g) { propRefs.current.set(p.id, g); if (p.id === selPropId) setSelPropObj(g); }
                                    else propRefs.current.delete(p.id);
                                }}
                                onSelect={(obj) => { setSelPropId(p.id); setSelPropObj(obj); }}
                            />
                        ))}
                        {!capturing && !flying && selPropObj && (
                            <>
                                <TransformControls object={selPropObj} mode={propGizmo} size={0.7} onMouseUp={commitPropTransform} />
                                {/* Always available, whichever mode the gizmo is in. */}
                                <UniformScaleHandle object={selPropObj} onCommit={commitPropTransform} controlsRef={controlsRef} />
                            </>
                        )}
                        {/* Phase 3 — free view: the shot camera as a scene object
                            (click to look through it, or pose it with the gizmo) + a
                            live PiP of its view. Editable unless a blockout is selected
                            (so the two gizmos never fight). */}
                        {camMode === 'edit' && !capturing && shotViewRef.current && (
                            <CameraObject view={shotViewRef.current} focalLength={focalLength} aspect={aspect} onEnter={toggleCamMode} editable={!selPropId} gizmoMode={camGizmoMode} onViewChange={(v) => { shotViewRef.current = v; }} />
                        )}
                        {camMode === 'edit' && !capturing && (
                            <PiPView viewRef={shotViewRef} focalLength={focalLength} />
                        )}
                        {/* Real camera depth-of-field. Bypassed during CAPTURE — post-
                            processing renders into an opaque buffer, which would destroy
                            the transparent alpha cutout; the plain pass grabs the product
                            on transparency. Focus follows the click; aperture → blur. */}
                        {!capturing && (
                            <EffectComposer multisampling={4}>
                                <DepthOfField target={focusTarget} worldFocusRange={dof.worldFocusRange} bokehScale={dof.bokehScale} />
                                {/* ACES tone mapping — WITHOUT this the composer outputs linear
                                    colour (postprocessing disables the renderer's own tone map),
                                    which blows out white products to a featureless white. The
                                    composer-free Capture keeps ACES, hence its detail; this
                                    restores the same rolloff in the live view. */}
                                <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
                                <BrightnessContrast brightness={-0.02} contrast={0.05} />
                            </EffectComposer>
                        )}
                        <OrbitControls
                            ref={controlsRef}
                            makeDefault
                            target={[0, 0.9, 0]}
                            enablePan
                            enableZoom
                            enableDamping
                            dampingFactor={0.1}
                            minDistance={1.2}
                            maxDistance={12}
                            // Keep the camera in the range interior photography
                            // actually lives in — a moderately-high angle (~40°
                            // from vertical) down to just below eye-level. Going
                            // fully overhead / below the floor produces a view
                            // Flux can't build a coherent room around.
                            minPolarAngle={Math.PI * 0.22}
                            maxPolarAngle={Math.PI * 0.52}
                        />
                    </Canvas>
                    </ErrorBoundary>

                    {/* From Reference alignment guide — a faint overlay of the
                        reference image so the vendor can pose the product to match
                        its object's position/angle. Purely visual: pointer-events
                        none (orbit still works) and NOT part of the WebGL capture. */}
                    {referenceImageUrl && refOpacity > 0 && (
                        <img
                            src={referenceImageUrl}
                            alt="Reference guide"
                            className="pointer-events-none absolute inset-0 h-full w-full object-cover select-none"
                            style={{ opacity: refOpacity }}
                        />
                    )}

                    {!resolvedUrl && !error && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <Loader2 className="size-6 animate-spin text-muted-foreground" />
                        </div>
                    )}

                    {/* Rule-of-thirds framing lines — part of "Grid" (centering guides).
                        Viewport chrome, not captured. White whatever the theme — they
                        sit over the 3D render, not over app chrome. */}
                    {showGrid && (
                        <div className="pointer-events-none absolute inset-0 z-[1]">
                            {[33.3333, 66.6666].map((p) => (
                                <React.Fragment key={p}>
                                    <div className="absolute top-0 bottom-0 w-px" style={{ left: `${p}%`, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                                    <div className="absolute right-0 left-0 h-px" style={{ top: `${p}%`, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {/* Free view — camera pose gizmo (move / rotate) top-left.
                        Hidden while a blockout is selected (its toolbar takes over). */}
                    {camMode === 'edit' && !capturing && !selPropId && (
                        <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                            <StudioIconToggle active={camGizmoMode === 'translate'} onClick={() => setCamGizmoMode('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE_CAMERA', 'Move camera')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                            </StudioIconToggle>
                            <StudioIconToggle active={camGizmoMode === 'rotate'} onClick={() => setCamGizmoMode('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE_CAMERA', 'Rotate camera')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                            </StudioIconToggle>
                        </div>
                    )}
                    {/* Selected library prop — move / rotate + delete. Scale is
                        not here: it is the blue handle on the piece itself, so it
                        needs no mode. Removal lives HERE, on the thing that is
                        selected, not only in a dock panel the vendor may not have
                        open. */}
                    {selPropId && !capturing && (
                        <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                            <StudioIconToggle active={propGizmo === 'translate'} onClick={() => setPropGizmo('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE', 'Move')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                            </StudioIconToggle>
                            <StudioIconToggle active={propGizmo === 'rotate'} onClick={() => setPropGizmo('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE', 'Rotate')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                            </StudioIconToggle>
                            <StudioToolbarDivider />
                            <StudioIconToggle active={false} onClick={() => deleteProp(selPropId)} label={t('CONTENT_STUDIO.STUDIO_DELETE', 'Delete')}>
                                <Trash2 />
                            </StudioIconToggle>
                        </div>
                    )}

                    {/* In-canvas gizmo toolbar — Rotate / Move mode + Reset (only
                        when the model gizmo is shown, and in shot view). */}
                    {modelObj && showGizmo && camMode === 'shot' && !selPropId && (
                        <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 left-2.5')}>
                            <StudioIconToggle active={transformMode === 'rotate'} onClick={() => setTransformMode('rotate')} label={t('CONTENT_STUDIO.STUDIO_ROTATE', 'Rotate')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
                            </StudioIconToggle>
                            <StudioIconToggle active={transformMode === 'translate'} onClick={() => setTransformMode('translate')} label={t('CONTENT_STUDIO.STUDIO_MOVE', 'Move')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" /></svg>
                            </StudioIconToggle>
                            <StudioToolbarDivider />
                            <StudioIconToggle active={false} onClick={resetTransform} label={t('CONTENT_STUDIO.STUDIO_RESET', 'Reset')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L3 8" /></svg>
                            </StudioIconToggle>
                        </div>
                    )}

                    {/* View toggles — camera/free view + grid + gizmo (top-right). */}
                    <div className={cn(STUDIO_TOOLBAR_CLASS, 'top-2.5 right-2.5')}>
                        <StudioIconToggle
                            active={camMode === 'shot'}
                            onClick={toggleCamMode}
                            label={camMode === 'shot' ? t('CONTENT_STUDIO.STUDIO_VIEW_FREE', 'Free view') : t('CONTENT_STUDIO.STUDIO_VIEW_SHOT', 'Look through camera')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>
                        </StudioIconToggle>
                        <StudioToolbarDivider />
                        <StudioIconToggle active={showGrid} onClick={() => setShowGrid((v) => !v)} label={showGrid ? 'Hide grid' : 'Show grid'}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="1.5" /><path d="M9 3v18M15 3v18M3 9h18M3 15h18" /></svg>
                        </StudioIconToggle>
                        <StudioIconToggle active={showGizmo} onClick={() => setShowGizmo((v) => !v)} label={showGizmo ? 'Hide controls' : 'Show controls'}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>
                        </StudioIconToggle>
                    </div>

                    {/* PiP frame — outlines the live camera preview (bottom-left);
                        click to look through the camera. */}
                    {camMode === 'edit' && !capturing && (
                        <div
                            onClick={toggleCamMode}
                            title={t('CONTENT_STUDIO.STUDIO_VIEW_SHOT', 'Look through camera')}
                            className="absolute bottom-3 left-3 z-[2] cursor-pointer rounded-lg border border-foreground/55 transition-colors hover:border-foreground"
                            style={{ width: 190, aspectRatio: `${aspect}` }}
                        >
                            <div className="pointer-events-none absolute -top-px -left-px bg-foreground/85 px-1.5 py-0.5 text-[10px] font-medium text-background">
                                {t('CONTENT_STUDIO.STUDIO_PIP_CAMERA', 'Camera')}
                            </div>
                        </div>
                    )}

                    {/* Viewport hint */}
                    <div className="pointer-events-none absolute right-0 bottom-3 left-0 text-center">
                        <span className="inline-block max-w-[70%] bg-background/80 px-2 py-0.5 text-[11px] text-muted-foreground">
                            {camMode === 'edit'
                                ? t('CONTENT_STUDIO.STUDIO_HINT_FREE', 'Free view — your shot stays framed. Drag the gizmo to pose the camera, or click it / the preview to look through it.')
                                : 'Drag to orbit · scroll to zoom · right-drag to pan · click the product to focus'}
                        </span>
                    </div>
                    </div>
                </div>

                {/* Close — floating over the viewport. */}
                <button
                    onClick={handleClose}
                    aria-label={t('CONTENT_STUDIO.STUDIO_CLOSE', 'Close')}
                    className="absolute top-3 right-3 z-[6] flex size-8 cursor-pointer items-center justify-center rounded-full border bg-background/90 text-muted-foreground backdrop-blur-md transition-colors hover:bg-background hover:text-foreground"
                >
                    <X className="size-[18px]" />
                </button>

                {/* Error chip — floats just above the dock. */}
                {error && (
                    <div className="absolute bottom-[88px] left-1/2 z-[6] max-w-[80%] -translate-x-1/2 rounded-lg border border-destructive/30 bg-background/95 px-3 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                {/* Bottom dock — all tools + the Capture action. */}
                <StudioDock
                    initialOpenKey={initialDock}
                    items={dockItems}
                    action={(
                        <Button
                            onClick={captureAndUse}
                            disabled={saving || !resolvedUrl}
                            variant="outline"
                            className="h-auto gap-1.5 bg-background/90 px-4 py-2.5 text-sm whitespace-nowrap backdrop-blur-xl"
                        >
                            {saving
                                ? <><Loader2 className="size-3.5 animate-spin" /> {t('CONTENT_STUDIO.STUDIO_CAPTURING', 'Capturing…')}</>
                                : <><Scan className="size-[15px]" /> {t('CONTENT_STUDIO.STUDIO_CAPTURE', 'Capture view')}</>}
                        </Button>
                    )}
                />
                </div>
            </div>
            </DialogContent>
        </Dialog>
    );
};

/** "tv stands" → "TV Stands". Category slugs are data, not display text. */
/**
 * Words that are initialisms, rather than words that happen to be short.
 *
 * The rule here used to be "two letters or fewer means uppercase", which reads
 * the language as acronyms: it got "tv stands" right and turned "my library"
 * into "MY Library". Across the categories that actually exist it served
 * exactly one real acronym and broke exactly one ordinary word — short words
 * are far more often words than initialisms, so this is a list now.
 */
const CATEGORY_ACRONYMS = new Set(['tv', 'av', 'pc', '3d', 'led', 'diy']);

const titleCase = (slug: string): string => slug
    .split(/[\s-]+/)
    .map((w) => (CATEGORY_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

/**
 * A left-rail button — icon over label, the whole thing a target.
 *
 * The rail carries what BUILDS the scene (furniture, the room shell); the bar
 * along the bottom carries properties of the SHOT (frame, lens, depth of
 * field). Keeping the two apart is the point of the rail: before, nine
 * unrelated things sat in one row and the two that change what is in the
 * picture looked exactly like the seven that change how it is photographed.
 */
export const StudioRailButton: React.FC<{
    active: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    /** Count of what is placed, when there is any. */
    badge?: number;
    /** A quiet on/off marker, for a mode rather than a count. */
    dot?: boolean;
}> = ({ active, onClick, icon, label, badge, dot }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
            'relative flex w-14 cursor-pointer flex-col items-center gap-0.5 rounded-md border-0 py-2 transition-colors [&_svg]:size-[21px]',
            active ? 'bg-muted text-foreground' : 'bg-transparent text-muted-foreground hover:bg-accent',
        )}
    >
        {icon}
        <span className="text-[10px] leading-tight font-medium">{label}</span>
        {badge !== undefined && (
            <span className="absolute top-1 right-1.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background">
                {badge}
            </span>
        )}
        {dot && badge === undefined && (
            <span className="absolute top-[7px] right-2 size-1.5 rounded-full bg-success" />
        )}
    </button>
);

/** Small square icon toggle for the in-canvas gizmo toolbar. */
export const StudioIconToggle: React.FC<{ active: boolean; onClick: () => void; label: string; children: React.ReactNode }> = ({ active, onClick, label, children }) => (
    <button
        onClick={onClick}
        title={label}
        aria-label={label}
        aria-pressed={active}
        className={cn(
            'flex size-7 cursor-pointer items-center justify-center rounded-md border-0 transition-colors [&_svg]:size-4',
            active
                ? 'bg-foreground/10 text-foreground hover:bg-foreground/15'
                : 'bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
    >
        {children}
    </button>
);

/**
 * On (re)mount of the Canvas, restore the previously-saved camera position +
 * orbit target so re-opening the dialog shows the exact framing the vendor left
 * (the Canvas remounts each open, otherwise it resets to the default view).
 */
const RestoreCamera: React.FC<{
    savedRef: React.MutableRefObject<{ pos: [number, number, number]; target: [number, number, number] } | null>;
    controlsRef: React.MutableRefObject<any>;
}> = ({ savedRef, controlsRef }) => {
    const camera = useThree((s) => s.camera);
    useEffect(() => {
        const saved = savedRef.current;
        if (!saved) return;
        camera.position.set(saved.pos[0], saved.pos[1], saved.pos[2]);
        const apply = () => {
            const c = controlsRef.current;
            if (c) {
                c.target.set(saved.target[0], saved.target[1], saved.target[2]);
                c.update();
            }
        };
        apply();
        // OrbitControls may mount a frame later — re-apply once more.
        const id = requestAnimationFrame(apply);
        return () => cancelAnimationFrame(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};

/** Applies a real photographic focal length (mm) to the active perspective camera. */
export const CameraRig: React.FC<{ focalLength: number }> = ({ focalLength }) => {
    const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera;
    useEffect(() => {
        if ((camera as any).isPerspectiveCamera) {
            camera.setFocalLength(focalLength);
            camera.updateProjectionMatrix();
        }
    }, [camera, focalLength]);
    return null;
};

// GLB loader — normalize any model to sit centered on the floor at a consistent size.
const StudioModel: React.FC<{ url: string; onPick?: (p: THREE.Vector3) => void; groupRef?: (g: THREE.Group | null) => void; layFlat?: boolean }> = ({ url, onPick, groupRef, layFlat }) => {
    const { scene } = useGLTF(url) as any;
    const gl = useThree((s) => s.gl);
    const { model: cloned, pivotY } = useMemo(() => {
        const c = scene.clone(true) as THREE.Group;
        // Rugs are flat floor coverings but their GLB is often authored standing
        // upright (the patterned face on the X/Y plane, thickness on Z). Left as
        // is, the studio shows it like a painting and a top-view capture reads as
        // a tilted floating board. Auto-lay it FLAT: rotate the thinnest axis
        // (the pile thickness) to vertical so the patterned face points up and the
        // rug rests on the floor at any camera angle.
        if (layFlat) {
            const b0 = new THREE.Box3().setFromObject(c);
            const s0 = new THREE.Vector3();
            b0.getSize(s0);
            const thin = Math.min(s0.x, s0.y, s0.z);
            if (thin === s0.z) c.rotateX(-Math.PI / 2); // upright plane (thin Z) → lie flat
            else if (thin === s0.x) c.rotateZ(Math.PI / 2); // thin X → lie flat
            // thin === s0.y: already lying flat, leave as is
            c.updateMatrixWorld(true);
        }
        const box = new THREE.Box3().setFromObject(c);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        c.scale.setScalar(2 / maxDim);
        const box2 = new THREE.Box3().setFromObject(c);
        const center = new THREE.Vector3();
        box2.getCenter(center);
        c.position.x -= center.x;
        c.position.z -= center.z;
        c.position.y -= box2.min.y;
        const py = (box2.max.y - box2.min.y) / 2 || 0.9; // model centre height (base at y=0)

        // Anisotropic filtering + mipmaps on every texture. Without this, ribbed/
        // striped fabrics (corduroy) alias into a "melty/wavy" mess at grazing
        // angles — the #1 texture artifact in captures. Uses the GPU's max
        // anisotropy (typically 16).
        const maxAniso = gl.capabilities.getMaxAnisotropy?.() || 1;
        const TEX_KEYS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'];
        c.traverse((obj: any) => {
            const material = obj.material;
            if (!material) return;
            // Self-shadowing: the model both casts and receives so shelf cavities,
            // undersides and overlaps develop real depth — essential for white
            // products to read as 3D on the white capture sweep.
            obj.castShadow = true;
            obj.receiveShadow = true;
            const mats = Array.isArray(material) ? material : [material];
            for (const m of mats) {
                for (const key of TEX_KEYS) {
                    const tex = m[key];
                    if (tex) {
                        tex.anisotropy = maxAniso;
                        tex.minFilter = THREE.LinearMipmapLinearFilter;
                        tex.magFilter = THREE.LinearFilter;
                        tex.generateMipmaps = true;
                        tex.needsUpdate = true;
                    }
                }
            }
        });
        return { model: c, pivotY: py };
    }, [scene, gl, layFlat]);
    // Click-to-focus: set the DOF focus point to where the user clicks on the
    // product. Object rotation pivots around the model's CENTRE (group at pivotY
    // with the model offset by -pivotY) so spin/tilt/roll turn it in place.
    // The rotatable group's origin sits at the model CENTRE (position at pivotY,
    // model offset by -pivotY), so the in-canvas gizmo spins/moves it in place.
    // groupRef hands the group up to the parent so TransformControls can attach.
    return (
        <group ref={groupRef} position={[0, pivotY, 0]}>
            <primitive
                object={cloned}
                position={[0, -pivotY, 0]}
                onClick={(e: any) => { e.stopPropagation(); if (e.point && onPick) onPick(e.point.clone()); }}
            />
        </group>
    );
};
